import { useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { setFilters, useFilters } from "@/state/filters.ts";
import { formatMinutes } from "../entry/RuntimeChip.tsx";
import styles from "./controls.module.css";

/**
 * The two toggles that change what a "stop" even is.
 *
 * **Series** is the blunt one: two thirds of the catalog by count is Disney+
 * seasons, and plenty of people simply won't watch television for a film.
 *
 * **Compressed routes** is the sleeper (PLAN §1): a curated recap or an
 * essential-episodes list keeps a thread's payoff at a couple of hours instead
 * of twelve. It only ever *lowers* what a stop costs, so it changes the runtime
 * cap and the totals, never the road's contents. The curation pass has written
 * those routes — the zero-state copy below is the fallback for a catalog that
 * hasn't, not the state the shipped catalog is in.
 */
export function MediaToggles() {
  const catalog = useCatalog();
  const filters = useFilters();

  const { seasons, compressible, savings } = useMemo(() => {
    let seasonCount = 0;
    let compressibleCount = 0;
    let saved = 0;
    for (const entry of catalog.entries) {
      if (entry.kind === "season") seasonCount += 1;
      const delta = entry.runtime.minutes - entry.solver.minMinutes;
      if (delta > 0) {
        compressibleCount += 1;
        saved += delta;
      }
    }
    return { seasons: seasonCount, compressible: compressibleCount, savings: saved };
  }, [catalog.entries]);

  return (
    <div className={styles.field}>
      <span className={styles.label}>Media</span>
      <label className={styles.switch}>
        <input
          type="checkbox"
          checked={filters.includeSeries}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, includeSeries: event.target.checked }))
          }
        />
        <span>
          Include series <span className={styles.chipCount}>{seasons}</span>
        </span>
      </label>
      <label className={styles.switch}>
        <input
          type="checkbox"
          checked={filters.useAlternatives}
          disabled={compressible === 0}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, useAlternatives: event.target.checked }))
          }
        />
        <span>
          Use recaps &amp; essential episodes
          {compressible > 0 && <span className={styles.chipCount}>{compressible}</span>}
        </span>
      </label>
      <p className={styles.hint}>
        {compressible === 0
          ? "No compressed routes are curated yet. Recaps and essential-episode lists land with the rest of the curation pass."
          : `Costs ${compressible} stops at their shortest curated route, saving up to ${formatMinutes(savings)}.`}
      </p>
    </div>
  );
}
